import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Typography,
} from "@material-ui/core"
import type { HourReportAdmin, HourReportAdminAssignmentOption } from "../../../../memberships/api/schemas"
import { MaterialSymbol } from "../../hour-reporting-roles/components/MaterialSymbol"
import { AdminSelector } from "./AdminSelector"
import type { HourReportingAdminsTranslations } from "../hooks/use-translations"
import React from "react"
import { useTranslation } from "react-i18next"

type CurrentAdminsPanelProps = {
  admins: HourReportAdmin[]
  options: HourReportAdminAssignmentOption[]
  selectedOption: HourReportAdminAssignmentOption | null
  canUpdateAdmins: boolean
  isUpdating: boolean
  isLoadingOptions: boolean
  isOptionsError: boolean
  translations: HourReportingAdminsTranslations
  onSelectedOptionChange: (option: HourReportAdminAssignmentOption | null) => void
  onAdd: () => void
  onRemove: (admin: HourReportAdmin) => void
}

export const CurrentAdminsPanel = ({
  admins,
  options,
  selectedOption,
  canUpdateAdmins,
  isUpdating,
  isLoadingOptions,
  isOptionsError,
  translations,
  onSelectedOptionChange,
  onAdd,
  onRemove,
}: CurrentAdminsPanelProps) => {
  const { t: tOrg } = useTranslation("org")
  const sortedAdmins = React.useMemo(() => {
    return [...admins].sort((a, b) => a.name.localeCompare(b.name))
  }, [admins])

  return (
    <Box mt={3}>
      <Paper variant="outlined">
        {canUpdateAdmins && (
          <>
            <Box p={2}>
              <Typography variant="subtitle1" gutterBottom>
                {translations.addAdminLabel}
              </Typography>
              {isOptionsError ? (
                <Typography variant="body2" color="error">
                  {translations.errorLoadingOptionsText}
                </Typography>
              ) : (
                <>
                  <AdminSelector
                    options={options}
                    value={selectedOption}
                    disabled={isLoadingOptions || isUpdating}
                    translations={translations}
                    onChange={onSelectedOptionChange}
                  />
                  <Box mt={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={
                        isUpdating ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <MaterialSymbol name="add" aria-hidden />
                        )
                      }
                      disabled={!selectedOption || isLoadingOptions || isUpdating}
                      onClick={onAdd}
                    >
                      {translations.addButtonLabel}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </>
        )}

        <Divider />

        {sortedAdmins.length ? (
          <>
            <Box p={2} mt={2}>
              <Typography variant="h6" component="h2">
                {translations.currentAdminsTitle}
              </Typography>
            </Box>
            <List disablePadding dense>
              {sortedAdmins.map((admin, index) => (
                <ListItem key={admin.id} divider={index < admins.length - 1}>
                  <ListItemText
                    primary={admin.name}
                    secondary={admin.email}
                    primaryTypographyProps={{ variant: "subtitle2" }}
                    secondaryTypographyProps={{ variant: "body2", color: "textSecondary" }}
                  />
                  {canUpdateAdmins && (
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label={tOrg("orgMembershipsPage.hourReportingAdmins.removeAdminLabel", {
                          defaultValue: "Remove {{memberName}} as an hour reporting admin",
                          memberName: admin.name,
                        })}
                        disabled={isUpdating}
                        onClick={() => onRemove(admin)}
                      >
                        <MaterialSymbol name="close" aria-hidden />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </List>
          </>
        ) : (
          <Box p={3}>
            <Typography variant="body2" color="textSecondary">
              {translations.noAdminsText}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  )
}
